import throttle from 'lodash/throttle';
import React, { useEffect, useRef, useState } from 'react';

import TimelineEvent from './TimelineEvent';
import { TIMELINE_DATA } from './constant';

const Timeline = () => {
  const [progressPercentage, setProgressPercentage] = useState(0);
  const [intersectingIndexes, setIntersectingIndexes] = useState<number[]>([]);
  const timelineRef = useRef<HTMLDivElement>(null);
  const progressRef = useRef<HTMLDivElement>(null);
  const circleRefs = useRef<(HTMLSpanElement | null)[]>([]);

  const handleScroll = () => {
    const element = timelineRef.current;
    const progressElement = progressRef.current;

    if (!element || !progressElement) return;

    const calculateScrollProgress = () => {
      const { top, height } = element.getBoundingClientRect();
      const midViewport = window.innerHeight / 2;
      const distanceScrolled = midViewport - top;
      return Math.max(0, Math.min(1, distanceScrolled / height)) * 100;
    };

    setProgressPercentage(calculateScrollProgress());

    const updateIntersectingIndexes = () => {
      const progressRect = progressElement.getBoundingClientRect();
      circleRefs.current.forEach((circle, index) => {
        if (!circle) return;

        const circleRect = circle.getBoundingClientRect();
        const isIntersecting = progressRect.bottom >= circleRect.top;

        setIntersectingIndexes((prev) => {
          if (isIntersecting && !prev.includes(index)) {
            return [...prev, index];
          } else if (!isIntersecting) {
            return prev.filter((i) => i !== index);
          }
          return prev;
        });
      });
    };

    updateIntersectingIndexes();
  };

  useEffect(() => {
    const throttledHandleScroll = throttle(handleScroll, 100);
    window.addEventListener('scroll', throttledHandleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', throttledHandleScroll, {
        passive: true,
      } as AddEventListenerOptions);
    };
  }, []);

  return (
    <div className="mx-auto max-w-[432px] pb-64 pt-[60px] sm:pt-72 md:max-w-[844px] md:pb-112 md:pt-[122px]">
      <h1 className="mb-8 mt-0 text-center font-suisse-works text-headline-md text-primary-black">
        Timeline
      </h1>
      <p className="mb-[68px] mt-0 text-center font-suisse-neue text-body-lg text-primary-black sm:mb-90">
        Key events in the history of The Information.
      </p>
      <div className="relative py-40 pl-20 md:pl-0">
        <div
          ref={timelineRef}
          className="absolute bottom-0 left-[30px] top-0 mx-auto w-2 overflow-y-clip bg-grayscale-beige before:absolute before:left-[-30px] before:top-0 before:h-px before:w-[60px] before:bg-grayscale-beige before:content-[''] after:absolute after:bottom-0 after:left-[-30px] after:h-px after:w-[60px] after:bg-grayscale-beige after:content-[''] before:sm:left-[-57px] before:sm:w-[114px] after:sm:left-[-57px] after:sm:w-[114px] md:left-0 md:right-0 dark:bg-[var(--light-grayscale-charcoal)] dark:before:bg-[var(--light-grayscale-charcoal)] dark:after:bg-[var(--light-grayscale-charcoal)]"
        >
          <div
            ref={progressRef}
            className="absolute left-0 top-0 h-full w-full bg-primary-magenta transition-transform duration-100 ease-linear"
            style={{
              transform: `translateY(${-100 + progressPercentage}%)`,
            }}
          />
        </div>
        <div className="flex flex-col gap-56">
          {TIMELINE_DATA.map((event, index) => (
            <TimelineEvent
              key={index}
              index={index}
              circleRef={(el) => (circleRefs.current[index] = el)}
              isIntersecting={intersectingIndexes.includes(index)}
              {...event}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default Timeline;
